import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, filter } from 'rxjs/operators';
import { Address } from '../../model/address';
import { Country } from '../../model/country';
import { County } from '../../model/county';
import { BaseFilter } from '../../model/filter/base-filter';
import { Locality } from '../../model/locality';
import { PageRequest } from '../../model/page/page-request';
import { Street } from '../../model/street';
import { CountryService } from '../../services/http/country.service';
import { CountyService } from '../../services/http/county.service';
import { LocalityService } from '../../services/http/locality.service';
import { StreetService } from '../../services/http/street.service';
import { AppConstants } from '../../util/app-constants';
import { normalizedText } from '../../util/utils';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
  standalone: false
})
export class AddressFormComponent implements OnInit, OnChanges {

  readonly countryEquals = Country.equals;
  readonly countyEquals = County.equals;
  readonly localityEquals = Locality.equals;
  readonly streetName = Street.showName;

  @Input() addressForm: FormGroup;//<AddressForm>;
  @Input() address: Address;

  availableCountries: Country[] = [];
  countriesFilterControl: FormControl = new FormControl();

  availableCounties: County[] = [];
  filteredCounties: County[] = [];
  countiesFilterControl: FormControl = new FormControl();

  availableLocalities: Locality[] = [];
  localitiesFilterControl: FormControl = new FormControl();

  availableStreets: Street[] = [];
  streetsFilterControl: FormControl = new FormControl();

  constructor(
    private readonly countryService: CountryService,
    private readonly countyService: CountyService,
    private readonly localityService: LocalityService,
    private readonly streetService: StreetService) { }

  ngOnInit(): void {
    this.initCountries();
    this.resetAddressChangedEmitters();

    const country: Country = this.addressForm.controls.country.value as Country;
    const county: County = this.addressForm.controls.county.value as County;
    const locality: Locality = this.addressForm.controls.locality.value as Locality;

    if (country) {
      this.findCounties(country);
    }
    if (county) {
      this.findCounties(country);
    }
    if (locality) {
      this.findLocalities(county, country, locality.name);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.address && changes.address.currentValue) {
      this.loadAddress(this.address);
    }
  }

  private loadAddress(address: Address): void {
    this.findCounties(address.country);
    this.findLocalities(address.county, address.country, address.locality.name);

    const addressForm = this.addressForm as FormGroup;
    addressForm.controls.country.patchValue(address.country, { emitEvent: false });
    addressForm.controls.county.patchValue(address.county, { emitEvent: false });
    addressForm.controls.locality.patchValue(address.locality, { emitEvent: false });
    addressForm.controls.street.patchValue(address.street, { emitEvent: false });
    addressForm.controls.streetNumber.patchValue(address.streetNumber, { emitEvent: false });
    addressForm.controls.building.patchValue(address.building, { emitEvent: false });
    addressForm.controls.buildingEntry.patchValue(address.buildingEntry, { emitEvent: false });
    addressForm.controls.buildingFloor.patchValue(address.buildingFloor, { emitEvent: false });
    addressForm.controls.buildingApartment.patchValue(address.buildingApartment), { emitEvent: false };
    addressForm.controls.address.patchValue(address.address, { emitEvent: false });
    addressForm.controls.postalCode.patchValue(address.postalCode || address.locality.postalCode, { emitEvent: false });
  }

  private initCountries(): void {
    this.countryService
      .findByCode('RO')
      .subscribe(country => {
        this.availableCountries = [country];
        if (!this.addressForm.controls.country.value) {
          this.addressForm.controls.country.patchValue(country);
        } else {
          this.findCounties(country);
        }
      });
  }

  private resetAddressChangedEmitters(): void {
    this.countriesFilterControl.valueChanges
      .pipe(debounceTime(AppConstants.DEFAULT_DEBOUNCE_TIME))
      .subscribe(keyword => this.findCountries(keyword));

    this.countiesFilterControl.valueChanges
      .pipe(debounceTime(AppConstants.DEFAULT_DEBOUNCE_TIME))
      .subscribe(keyword => this.filteredCounties = this.availableCounties
        .filter(county => normalizedText(county.name.toLowerCase()).includes(normalizedText(keyword.toLowerCase()))));

    this.localitiesFilterControl.valueChanges
      .pipe(debounceTime(AppConstants.DEFAULT_DEBOUNCE_TIME))
      .subscribe(keyword => {
        const country: Country = this.addressForm.controls.country.value as Country;
        const county: County = this.addressForm.controls.county.value as County;
        this.findLocalities(county, country, keyword);
      });

    this.addressForm.controls.country.valueChanges.subscribe((country: Country) => {
      this.findCounties(country);

      this.addressForm.controls.county.patchValue(null);
      this.addressForm.controls.county.updateValueAndValidity();
      this.addressForm.controls.locality.patchValue(null, { emitEvent: false });

      this.resetAddressFields();
    });
    this.addressForm.controls.county.valueChanges.subscribe((county: County) => {
      const country: Country = this.addressForm.controls.country.value as Country;
      this.findLocalities(county, country, null);

      this.addressForm.controls.locality.patchValue(null, { emitEvent: false });
      this.resetAddressFields();
    });
    this.addressForm.controls.locality.valueChanges.subscribe((locality: Locality) => {
      this.resetAddressFields();
      if (locality?.postalCode) {
        this.addressForm.controls.postalCode.patchValue(locality.postalCode);
      }
    });

    this.addressForm.controls.street.valueChanges
      .pipe(filter(street => street))
      .subscribe((street: Street | string) => {
        if (typeof street === 'string') {
          const locality: Locality = this.addressForm.controls.locality.value as Locality;
          if (locality) {
            this.findStreets(locality, street);
          }
          this.addressForm.controls.street.patchValue(Street.of(street), { emitEvent: false });
        } else if (street.postalCode) {
          this.addressForm.controls.postalCode.patchValue(street.postalCode, { emitEvent: false });
        }
      });
  }

  onStreetSelected(event: MatAutocompleteSelectedEvent): void {
    const street = event.option.value as Street;
    this.addressForm.controls.street.patchValue(street, { emitEvent: false });
    if (street.postalCode) {
      this.addressForm.controls.postalCode.patchValue(street.postalCode, { emitEvent: false });
    }
  }

  private findCountries(keyword: string): void {
    this.countryService
      .findByCode('RO')
      .subscribe(country => this.availableCountries = [country]);
  }

  private findCounties(country: Country): void {
    this.countyService
      .findCounties(country.code, PageRequest.maxPage())
      .subscribe(countiesPage => {
        this.availableCounties = countiesPage.content;
        this.filteredCounties = [...this.availableCounties];
      });
  }

  private findLocalities(county: County, country: Country, keyword: string): void {
    this.localityService
      .findLocalities(country.code, county?.code, BaseFilter.withKeyword(keyword), PageRequest.firstPage())
      .subscribe(localitiesPage => this.availableLocalities = localitiesPage.content);
  }

  private findStreets(locality: Locality, keyword: string): void {
    this.streetService
      .findStreets(locality.uuid, keyword, PageRequest.firstPage())
      .subscribe(streetsPage => this.availableStreets = streetsPage.content);
  }

  private resetAddressFields(): void {
    this.addressForm.controls.address.patchValue(null);
    this.addressForm.controls.streetNumber.patchValue(null);
    this.addressForm.controls.street.patchValue(null);
    this.addressForm.controls.building.patchValue(null);
    this.addressForm.controls.buildingEntry.patchValue(null);
    this.addressForm.controls.buildingFloor.patchValue(null);
    this.addressForm.controls.buildingApartment.patchValue(null);
    this.addressForm.controls.postalCode.patchValue(null);
  }

}
